/* Geral / Bibliotecas */
import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

/* Cores */
import { COLOR_DARK2 } from "../../Colors";

/* Métodos / Ações */
import { alterarEmissaoVencimentoDocumento } from "../../actions/documentos";
import { clearErrors } from "../../actions/errors";
import { setModalOpen } from "../../actions/modal";

/* Componentes */
import LoadingImage from "../LoadingImage";
import { DateInput } from "../CustomFields/DateInput";

/* Botões */
import ButtonPrimary from "../Buttons/ButtonPrimary";

/* Validação */
import { dateField } from "../../validation/formValidation";

const MySwal = withReactContent(Swal);

const parseDate = (value) => {
    if (!value) return "";

    const date = moment(value.date || value);

    return date.isValid() ? date.toDate() : "";
};

const getDateValue = (value) => {
    if (!value) return "";

    const date = moment(value.date || value);

    return date.isValid() ? date.format("YYYY-MM-DD") : "";
};

const DateField = ({
    name,
    label,
    selected,
    disabled = false,
    validate,
    setFieldValue,
}) => (
    <div className="form-group">
        <label
            className="w-100 mb-1"
            htmlFor={name}
            style={{ fontSize: "14px", color: "#555555" }}
        >
            {label}
        </label>
        <Field name={name} validate={validate}>
            {() => (
                <DateInput
                    name={name}
                    id={name}
                    selected={selected}
                    disabled={disabled}
                    onChange={(date) => {
                        setFieldValue(name, date);
                    }}
                />
            )}
        </Field>
        <ErrorMessage
            name={name}
            component="small"
            className="form-text text-danger"
        />
    </div>
);

class AlterarEmissaoVencimento extends Component {
    componentDidMount() {
        if (!this.props.dados?.documento?.nome || !this.props.envio?.idDocumentoEnvio) {
            this.props.dispatch(setModalOpen(""));
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.errors?.message) {
            MySwal.fire({
                type: "error",
                title: nextProps.errors.message,
                toast: true,
                position: "center",
                showConfirmButton: false,
                timer: 3000,
            });
            this.props.dispatch(clearErrors());
        }
    }

    isValidadeManual() {
        return Number(this.props.dados?.documento?.validade || 0) <= 0;
    }

    getDataVencimentoValue(values) {
        if (this.isValidadeManual()) {
            return values.dataVencimento;
        }

        if (!values.dataEmissao) {
            return parseDate(this.props.envio?.validade);
        }

        const validade = Number(this.props.dados?.documento?.validade || 0);
        const dataVencimento = moment(values.dataEmissao).add(validade, "days");

        return dataVencimento.isValid() ? dataVencimento.toDate() : "";
    }

    handleSubmit(values, actions) {
        this.props.dispatch(
            alterarEmissaoVencimentoDocumento(
                values,
                this.props.dados,
                actions
            )
        );
    }

    hasDateChanged(values, initialValues) {
        const emissaoChanged =
            getDateValue(values.dataEmissao) !== getDateValue(initialValues.dataEmissao);
        const vencimentoChanged =
            this.isValidadeManual() &&
            getDateValue(values.dataVencimento) !== getDateValue(initialValues.dataVencimento);

        return emissaoChanged || vencimentoChanged;
    }

    render() {
        if (
            !this.props.dados?.documento?.nome ||
            !this.props.envio?.idDocumentoEnvio
        ) {
            return <LoadingImage />;
        }

        const validadeManual = this.isValidadeManual();
        const initialValues = {
            dataEmissao: parseDate(this.props.envio.dataEmissao),
            dataVencimento: parseDate(this.props.envio.validade),
        };

        return (
            <div className="pb-5" style={{ padding: "20px 31px 0" }}>
                <fieldset
                    style={{
                        border: "1px solid #d1d1d1",
                        padding: "14px 8px 13px",
                        marginBottom: "28px",
                    }}
                >
                    <legend
                        style={{
                            width: "auto",
                            padding: "0 6px",
                            marginBottom: 0,
                            fontSize: "18px",
                            color: "#555555",
                        }}
                    >
                        Documento
                    </legend>
                    <div
                        className="font-weight-400"
                        style={{
                            color: COLOR_DARK2,
                            fontSize: "22px",
                            lineHeight: 1.25,
                            textTransform: "uppercase",
                        }}
                    >
                        {this.props.dados.documento.nome}
                    </div>
                </fieldset>
                <Formik
                    enableReinitialize={true}
                    initialValues={initialValues}
                    onSubmit={this.handleSubmit.bind(this)}
                    render={({
                        isSubmitting,
                        setFieldValue,
                        values,
                        handleSubmit,
                    }) => (
                        <Form onSubmit={handleSubmit}>
                            <div className="row" style={{ marginBottom: "12px" }}>
                                <div className="col-12 col-md-6 pr-md-3">
                                    <DateField
                                        name="dataEmissao"
                                        label="Data Emissão *"
                                        selected={values.dataEmissao}
                                        validate={dateField}
                                        setFieldValue={setFieldValue}
                                    />
                                </div>
                                <div className="col-12 col-md-6 pl-md-3">
                                    <DateField
                                        name="dataVencimento"
                                        label={validadeManual ? "Data de Validade *" : "Data de Validade"}
                                        selected={this.getDataVencimentoValue(values)}
                                        disabled={!validadeManual}
                                        validate={validadeManual ? dateField : undefined}
                                        setFieldValue={setFieldValue}
                                    />
                                    {!validadeManual && (
                                        <small className="form-text text-muted">
                                            A validade é calculada automaticamente a partir da data de emissão.
                                        </small>
                                    )}
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 d-flex align-items-center justify-content-center pt-2">
                                    <ButtonPrimary
                                        type="submit"
                                        disabled={
                                            isSubmitting ||
                                            !this.hasDateChanged(values, initialValues)
                                        }
                                        className="px-5 py-2"
                                        style={{
                                            minWidth: "315px",
                                            fontSize: "14px",
                                        }}
                                    >
                                        <FontAwesomeIcon icon="calendar-alt" />{" "}
                                        Alterar Emissão/Vencimento
                                    </ButtonPrimary>
                                </div>
                            </div>
                        </Form>
                    )}
                />
            </div>
        );
    }
}

AlterarEmissaoVencimento.propTypes = {
    errors: PropTypes.object.isRequired,
    dados: PropTypes.any,
    envio: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
    dados: state.dados.data,
    envio: state.envios.envio,
    errors: state.errors,
});

export default connect(mapStateToProps)(AlterarEmissaoVencimento);
